<template>
    <div class="footer-section">
        <div class="terms" @click="checked = !checked">
            <PrimeCheckbox 
                class="mt-1" 
                v-model="checked" 
                binary 
                @click.stop 
                @keydown.enter="checked = !checked"
                @keydown.space.prevent="checked = !checked"
                :tabindex="0"
                aria-label="Agree to Terms of Service and Privacy Policy"
            />
            <p>
                I agree to the
                <a href="https://privacybee.com/terms-of-service/" target="_blank" @click.stop> Terms of Service </a>
                &
                <a href="https://privacybee.com/privacy-policy/" target="_blank" @click.stop> Privacy Policy </a>
            </p>
        </div>

        <!-- Error Message Display -->
        <div v-if="errorMessage" class="error-message-box">
            <i class="pi pi-exclamation-circle"></i>
            <div class="error-content">
                <p class="error-text">{{ errorMessage }}</p>
                <p v-if="!isPromoCodeError" class="error-help">Please check with your card provider or try a different card.</p>
            </div>
        </div>

        <!-- Existing Payment Method Section (hide when receiving credit) -->
        <div v-if="hasExistingPaymentMethod && !isCredit && !isFreePurchase" class="payment-method-section">
            <div 
                class="existing-payment" 
                @click="useExistingPayment = true"
                @keydown.enter="useExistingPayment = true"
                @keydown.space.prevent="useExistingPayment = true"
                :tabindex="1"
                role="button"
                aria-label="Use existing payment method"
                :aria-pressed="useExistingPayment === true"
            >
                <PrimeRadioButton 
                    v-model="useExistingPayment" 
                    :value="true" 
                />
                <div class="payment-info">
                    <span class="payment-text">Pay with {{ authStore.user?.identity?.payment_method?.type?.toUpperCase() }} ending {{ authStore.user?.identity?.payment_method?.last_four }}</span>
                </div>
            </div>
            <div 
                class="new-payment"  
                @click="useExistingPayment = false"
                @keydown.enter="useExistingPayment = false"
                @keydown.space.prevent="useExistingPayment = false"
                :tabindex="2"
                role="button"
                aria-label="Enter new card details"
                :aria-pressed="useExistingPayment === false"
            >
                <PrimeRadioButton 
                    v-model="useExistingPayment" 
                    :value="false" 
                />
                <div class="payment-info">
                    <span class="payment-text">Enter new card details</span>
                </div>
            </div>
        </div>
        
        <div class="w-full">
            <PrimeButton
                :label="buttonText"
                :disabled="!checked"
                @click="handlePurchase"
                class="w-full"
                :loading="loader"
                />
        </div>
        <span class="usd-disclaimer"> All prices are in USD </span>
    </div>
</template>
<script setup lang="ts">
import PrimeButton from 'primevue/button';
import PrimeCheckbox from 'primevue/checkbox';
import PrimeRadioButton from 'primevue/radiobutton';
import { useDynamicDialog } from '~/composables/dynamic-dialog';
import { licenseService } from '~/services/license-service';
import { useSubscribeStore } from '~/stores/components/modals/subscription-modal';
import { usePersonStore } from '~/stores/person';
import { useBillingStore } from '~/stores/person/billing';
import { useContactsStore } from '~/stores/person/contacts';
import { LicenseRenewalTermKey, PurchaseType, isLicenseDowngrade } from '~/types/licenses/licenses';
import { ModalNames } from '~/types/modals';

const checked = ref(false);
const { showSuccess, showError, showWarning } = useGlobalToast();
const authStore = useAuthStore();
const store = useSubscribeStore();
const nuxtApp = useNuxtApp();
const loader = ref(false);
const dialogRef = getDialogRef(ModalNames.PURCHASE_LICENSES);
const personStore = usePersonStore();
const { selectedTerm } = storeToRefs(useSubscribeStore());
const paymentMethodId = ref('');
const useExistingPayment = ref(true);
const billingStore = useBillingStore();
const errorMessage = ref<string | null>(null);

const hasExistingPaymentMethod = computed(() => {
    return authStore.user?.identity?.payment_method?.type;
});

const isPromoCodeError = computed(() => {
    if (!errorMessage.value) return false;
    const msg = errorMessage.value.toLowerCase();
    return msg.includes('promo') || msg.includes('promo code');
});

const {
    license,
    selectedPlanType,
    purchaseType,
    totalCostAfterDiscounts
} = storeToRefs(store);

// Check if user is receiving a credit (negative amount)
const isCredit = computed(() => totalCostAfterDiscounts.value < 0);

// Check if purchase is fully covered by promo/credits (no payment needed)
const isFreePurchase = computed(() => totalCostAfterDiscounts.value <= 0);

// Button text changes based on whether it's a credit or payment
const buttonText = computed(() => {
    if (isFreePurchase.value) {
        return 'Confirm';
    }
    if (purchaseType.value === PurchaseType.UPGRADE && isCredit.value) {
        return 'Confirm';
    }
    return 'Purchase';
});

const closeDialog = () => {
    dialogRef.value.close();
};

const handlePurchase = () => {
    // If receiving a credit or purchase is fully covered, no payment method needed
    if (isCredit.value || isFreePurchase.value) {
        handleSubscription();
        return;
    }
    
    if (useExistingPayment.value && hasExistingPaymentMethod.value) {
        // Use existing payment method - call handleSubscription directly
        handleSubscription();
    } else {
        // Open payment modal for new card details
        handleShowPaymentModal();
    }
};

const handleShowPaymentModal = () => {
    // Reset payment method ID before opening modal
    paymentMethodId.value = '';
    
    // Listen for payment success using Nuxt hook system
    const nuxtApp = useNuxtApp();
    const hook = nuxtApp.hook('payment_method:success', (payload: { paymentMethodId: string }) => {
        paymentMethodId.value = payload.paymentMethodId;
        handleSubscription();
        // Remove the hook after it's used
        hook();
    });
    
    useDynamicDialog(ModalNames.STRIPE_PAYMENT);
}

const handleSubscription = async () => {
    loader.value = true;
    errorMessage.value = null; // Clear any previous errors

    if (purchaseType.value === PurchaseType.UPGRADE) {
        const upgradeData: any = {
            new_license: store.selectedPlanType,
            term: selectedTerm.value
        };

        // Include promo code if applied
        if (store.promoCodeApplied && store.promoCode) {
            upgradeData.promo_code = store.promoCode;
        }

        try {
            const result = await licenseService.changeLicense(authStore.user?.identity?.id!, license.value?.id!, upgradeData);

            personStore.getPerson();
            authStore.getMe();
            useBillingStore().fetchInvoices();
            store.clearPromoCode();
            closeDialog();

            // Check if payment failed
            if (result?.data?.status === 'failed') {
                showError('Error', 'Payment failed. Please check your card details and try again.');
            } else {
                const downgrading = isLicenseDowngrade(license.value?.slug ?? license.value?.type, store.selectedPlanType);
                showSuccess('Success', downgrading ? 'Downgrade scheduled for your next renewal.' : 'License modified successfully!');
            }
        } catch (error: any) {
            showError('Error', error.message || 'Failed to upgrade license');
        } finally {
            loader.value = false;
        }
        return;
    }

    // Handle regular purchase case
    const data: {
        subscription_type: string;
        licenses: {
            license: string;
            count: number;
        };
        payment_method?: string;
        promo_code?: string;
    } = {
        subscription_type: LicenseRenewalTermKey[selectedTerm.value],
        licenses: {
            license: store.selectedPlanType.toLowerCase(),
            count: store.licenseCount,
        },
    };

    // Only include payment_method if we're using a new card
    if (paymentMethodId.value) {
        data.payment_method = paymentMethodId.value;
    }

    // Include promo code if applied
    if (store.promoCodeApplied && store.promoCode) {
        data.promo_code = store.promoCode;
    }

    if ( !isFreePurchase.value && !hasExistingPaymentMethod.value && !paymentMethodId.value) {
        errorMessage.value = 'There was an issue creating your payment method.';
        loader.value = false;
        return;
    }

    try {
        const result = await licenseService.purchaseLicenses(authStore.user?.identity?.id!, data);

        // Check if 3D Secure authentication is required
        if (result && result.data && result.data.requires_action && result.data.payment_intent_client_secret) {
            // Handle 3D Secure authentication
            await handle3DSecureAuthentication(result.data);
            return;
        }

        const paymentFailed = result?.data?.status === 'failed';

        // For GIFT purchases, we need to await getPerson to ensure the new license is available
        // before attempting to assign it
        if (dialogRef.value?.data?.type === PurchaseType.GIFT) {
            await personStore.getPerson();
        } else {
            personStore.getPerson();
        }
        authStore.getMe();
        billingStore.fetchInvoices();

        // Clear promo code after successful purchase
        store.clearPromoCode();

        // Handle gift purchase - assign the newly purchased license to the contact
        if (dialogRef.value?.data?.type === PurchaseType.GIFT) {
            const contact = dialogRef.value?.data?.contact;
            if (!contact) {
                errorMessage.value = 'Contact information not found for license assignment.';
                closeDialog();
                return;
            }

            // Find the newly purchased license that's not yet assigned
            const license = personStore.person?.licenses.find(license => {
                return license.slug === store.selectedPlanType.toLowerCase() &&
                       license.term === selectedTerm.value &&
                       license.expired === false &&
                       !license.assigned_to;
            });

            if (!license) {
                errorMessage.value = 'Could not find the purchased license to assign. Please try assigning it manually from your billing page.';
                closeDialog();
                return;
            }

            await store.assignLicense(license.id, contact, true);
            await useContactsStore().fetchContacts();

            closeDialog();
            return;
        }

        closeDialog();
        if (paymentFailed) {
            showError('Error', 'Payment failed. Please check your card details and try again.');
        } else {
            showSuccess('Success', 'Congratulations! License(s) purchased successfully.');
        }
    } catch (error: any) {
        errorMessage.value = error.message || 'Failed to purchase licenses';
    } finally {
        loader.value = false;
    }
};

const handle3DSecureAuthentication = async (result: any) => {
    try {
        const stripeModule = await import('@stripe/stripe-js');
        const stripe = await stripeModule.loadStripe(useRuntimeConfig().public.pb.stripeKey);
        
        const { error, paymentIntent } = await stripe!.confirmCardPayment(result.payment_intent_client_secret);
        
        if (error) {
            showError('Error', error.message || '3D Secure authentication failed');
            loader.value = false;
            return;
        }

        if (paymentIntent.status === 'succeeded') {
            // Confirm the payment with our backend
            const paymentResult = await licenseService.confirmPayment(authStore.user?.identity?.id!, {
                payment_intent_id: paymentIntent.id,
                invoice_id: result.invoice_id
            });

            const paymentFailed = paymentResult?.data?.status === 'failed';

            // For GIFT purchases, we need to await getPerson to ensure the new license is available
            if (dialogRef.value?.data?.type === PurchaseType.GIFT) {
                await personStore.getPerson();
            } else {
                personStore.getPerson();
            }
            authStore.getMe();
            useBillingStore().fetchInvoices();

            // Handle gift purchase - assign the newly purchased license to the contact
            if (dialogRef.value?.data?.type === PurchaseType.GIFT) {
                const contact = dialogRef.value?.data?.contact;
                if (!contact) {
                    showError('Error', 'Contact information not found for license assignment.');
                    closeDialog();
                    return;
                }

                const license = personStore.person?.licenses.find(license => {
                    return license.slug === store.selectedPlanType.toLowerCase() &&
                           license.term === selectedTerm.value &&
                           license.expired === false &&
                           !license.assigned_to;
                });
                if (!license) {
                    showError('Error', 'Could not find the purchased license to assign. Please try assigning it manually from your billing page.');
                    closeDialog();
                    return;
                }

                await store.assignLicense(license.id, contact, true);
                await useContactsStore().fetchContacts();

                // Clear promo code and cookie after successful 3D Secure purchase
                store.clearPromoCode();
                closeDialog();
                return;
            }

            // Clear promo code and cookie after successful 3D Secure purchase
            store.clearPromoCode();

            closeDialog();
            if (paymentFailed) {
                showError('Error', 'Payment failed. Please check your card details and try again.');
            } else {
                showSuccess('Success', 'Congratulations! License(s) purchased successfully.');
            }
        } else {
            showError('Error', 'Payment was not successful');
        }
    } catch (error: any) {
        showError('Error', error.message || 'Failed to complete 3D Secure authentication');
    } finally {
        loader.value = false;
    }
};

</script>
<style lang="scss" scoped>
.footer-section {
    @apply flex flex-col items-start gap-2.5 pt-5 self-stretch;

    span {
        @apply self-stretch text-zinc-500 text-center font-inter text-xs font-normal leading-6;
    }

    .terms {
        @apply flex pb-2 items-start gap-2 cursor-pointer select-none;

        p {
            @apply text-zinc-500 font-inter text-sm font-normal leading-5;
        }

        a {
            @apply text-blue-600 underline;
        }
    }

    .error-message-box {
        @apply flex items-start gap-3 w-full p-4 bg-red-50 border border-red-200 rounded-lg;
        
        .pi {
            @apply text-red-500 text-lg mt-0.5 flex-shrink-0;
        }

        .error-content {
            @apply flex flex-col gap-1 flex-1;

            .error-text {
                @apply text-red-800 font-inter text-sm font-semibold leading-5 m-0;
            }

            .error-help {
                @apply text-red-600 font-inter text-xs font-normal leading-4 m-0;
            }
        }
    }

    .payment-method-section {
        @apply flex flex-col items-start gap-2 w-full;

        .existing-payment, .new-payment {
            @apply flex items-center gap-2 w-full cursor-pointer;
            @apply rounded-lg p-2 transition-all duration-200;
            
            &:focus,
            &:focus-visible {
                @apply outline-none ring-2 ring-blue-500 ring-offset-1;
            }
            
            &:hover {
                @apply bg-blue-100;
            }
        }

        .payment-info {
            @apply flex-1;

            .payment-text {
                @apply text-zinc-500 font-inter text-sm font-normal leading-5;
            }
        }
    }
}

.usd-disclaimer {
    @apply text-xs text-zinc-500 font-inter mt-2;
}
</style>
